<template>
  <div
    class="steps"
    :class="{
      'no-navigate': !props.canNavigate,
      'steps--has-labels': hasLabels,
      [props.customCssClass]: !!props.customCssClass
    }"
  >
    <div
      v-for="(step, index) in steps"
      :key="step?.number"
      class="step"
      :class="{ 'step--has-label': !!getStepLabel(step) }"
      @click="goToStep(step?.number)"
      @mouseover="showStepNumber(step?.number)"
      @mouseleave="hideStepNumber(step?.number)"
    >
      <div class="step-container">
        <div
          :class="['step-number', {
            'active': currentStep === step?.number,
            'completed': isStepCompleted(step?.number) && currentStep !== step?.number,
            'hovered': stepHovered.includes(step?.number)
          }]"
        >
          <template v-if="isStepCompleted(step?.number) && currentStep !== step?.number && !stepHovered.includes(step?.number)">
            <NavigationCheck class="completed-icon" />
          </template>
          <template v-else-if="step?.number >= steps.length">
            <span v-if="lastStepIsNumber">{{ step?.number }}</span>
            <Flag
              v-else
              class="flag-icon"
            />
          </template>
          <template v-else>
            {{ step?.number }}
          </template>
          <ToolTip
            v-show="stepHovered.includes(step?.number)"
            :show-tool-tip="showToolTip"
            tool-tip-position="bottom"
            class="tooltip"
            :hide-arrow="true"
          >
            {{ step?.title }}
          </ToolTip>
        </div>
        <span
          v-if="getStepLabel(step)"
          class="step-label"
          :class="{
            'active': currentStep === step?.number,
            'completed': isStepCompleted(step?.number) && currentStep !== step?.number
          }"
        >
          {{ getStepLabel(step) }}
        </span>
      </div>
      <div
        v-if="index < steps?.length - 1"
        :class="['step-dashes', { 'completed-dashes': isStepCompleted(step?.number) }]"
      >
        <span
          class="dash"
          v-for="dot in 3"
          :key="dot"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import NavigationCheck from '~/assets/icons/navigation_check.svg?component'
import Flag from '~/assets/icons/flag.svg?component'

const props = defineProps({
  steps: {
    type: Array<any>,
    default() {
      return []
    }
  },
  customCssClass: {
    type: String,
    default() {
      return ''
    }
  },
  completedSteps: {
    type: Array<number>,
    default() {
      return []
    }
  },
  currentStep: {
    type: Number,
    default() {
      return 1
    }
  },
  lastStepIsNumber: {
    type: Boolean,
    default() {
      return false
    }
  },
  showToolTip: {
    type: Boolean,
    default() {
      return true
    }
  },
  canNavigate: {
    type: Boolean,
    default() {
      return true
    }
  }
})

const emit = defineEmits(['goToStep'])

const goToStep = async (stepNumber: number) => {
  if (props.canNavigate && props.completedSteps.includes(stepNumber) || stepNumber === props.currentStep) {
    emit('goToStep', stepNumber)
  }
}

const stepHovered = ref<number[]>([])

const getStepLabel = (step: any): string => {
  return step?.label || step?.name || ''
}

const hasLabels = computed(() => {
  return props.steps.some((step: any) => !!getStepLabel(step))
})

const isStepCompleted = (stepNumber: number) => {
  return props.completedSteps.includes(stepNumber)
}

const showStepNumber = (stepNumber: number) => {
  if (!stepHovered.value.includes(stepNumber)) {
    stepHovered.value.push(stepNumber)
  }
}

const hideStepNumber = (stepNumber: number) => {
  const index = stepHovered.value.indexOf(stepNumber)
  if (index > -1) {
    stepHovered.value.splice(index, 1)
  }
}
</script>

<style lang="scss">
.steps {
    display: flex;
    align-items: center;
    margin-bottom: 1rem;

    &.no-navigate {
      .step .step-number.completed:hover {
        cursor: default;
        background-color: $color__fst__primary;
        border: .12rem solid $color__fst__primary;
      }
    }

    &.steps--has-labels {
      align-items: flex-start;

      .step {
        align-items: flex-start;

        .step-dashes {
          margin-top: 0.55rem;
        }
      }

      &.claim-steps__stepper {
        .step {
          .step-dashes {
            margin-top: 0.65rem;
          }
        }
      }
    }

    &.claim-steps__stepper {
      .step {
        .step-container {
          margin-right: 0;
        }

        .step-dashes {
          margin-inline: 0.45rem;
        }

        .step-number {
          width: 1.5rem;
          height: 1.5rem;
        }
      }
    }

    .step {
      display: flex;
      align-items: center;

      .step-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        margin-right: .2rem;
      }

      .step-label {
        font-size: 0.875rem;
        line-height: 1.1375rem;
        margin-top: 0.25rem;
        color: $color__fst__blue__100;
        text-align: center;
        white-space: nowrap;

        &.active {
          color: $color__fst__primary;
        }

        &.completed {
          color: $color__fst__primary;
        }
      }

      .step-number {
        width: 1.3rem;
        height: 1.3rem;
        border: .12rem solid $color__fst__blue__100;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: $color__fst__white;
        color: $color__fst__blue__100;
        font-weight: bold;
        font-size: 1rem;
        position: relative;

        &.active {
          background-color: $color__fst__cyan__100;
          color: $color__fst__white;
          border: .12rem solid $color__fst__cyan__100;

          svg {
            fill: $color__fst__white;
          }

          .flag-icon {
            fill: $color__fst__white;

            svg {
              width: .75rem;
              height: .875rem;
            }
          }
        }

        &.hovered {
          background-color: $color__fst__cyan__100;
          color: $color__fst__white;
          border: .12rem solid $color__fst__cyan__100;

          .flag-icon {
            fill: $color__fst__white;
          }
        }

        &.completed {
          background-color: $color__fst__primary;
          color: $color__fst__white;
          border: .12rem solid $color__fst__primary;
          cursor: pointer;

          &:hover {
            background-color: $color__fst__cyan__100;
            color: $color__fst__white;
            border: .12rem solid $color__fst__cyan__100;
          }
        }

        svg {
          width: 1rem;
          height: 1rem;
          fill: $color__fst__white;
        }

        .completed-icon {
          fill: $color__fst__white;
        }

        &.completed svg {
          fill: $color__fst__white;
        }

        &:not(.completed) svg {
          fill: $color__fst__blue__100;
        }

        .tooltip {
          width: auto;
          white-space: nowrap;
          top: 1.5rem;
          left: -30%;
          background-color: $color__fst__gray__020;
          color: $color__fst_gray__100;
          font-weight: $base-text-font-weight-light;
          font-size: .75rem;
          padding: .2rem .5rem;
        }
      }

      .step-dashes {
        display: flex;
        align-items: center;
        margin-right: .2rem;

        .dash {
          width: 0.25rem;
          height: 0.25rem;
          border-radius: 50%;
          background-color: $color__fst__blue__100;
          margin: 0 0.05rem;
        }
      }

      .completed-dashes .dash {
        background-color: $color__fst__primary;
      }
    }
  }
</style>
